import {
  call, select, takeLatest, put,
} from 'redux-saga/effects';
import vpsOciTypes from './vpsOci.types';
import { api } from '@/utils/api';
import { vpsOciActions } from '.';
import {
  PRODUCT_VPSOCI_URL, PRODUCT_VPSOCI_STATUS_URL, PRODUCT_REBOOT_URL, PRODUCT_VPSOCI_REBUILD_URL, PRODUCT_VPSOCI_CHANGE_IMAGE_URL, PRODUCT_SAVE_PASSWORD_URL, PRODUCT_OPEN_SSH_URL, PRODUCT_VPSOCI_TOUR_URL, PRODUCT_VPSOCI_TOUR_DETAIL_URL, PRODUCT_VPSOCI_UPGRADE_LISTING_URL, PRODUCT_VPSOCI_UPGRADE_DETAILS_URL, PRODUCT_VPSOCI_UPGRADE_URL, PRODUCT_VPSOCI_CONFIGURE_URL,
} from '@/config/api';
import { commonActions } from '../../common';
import { locale } from '@/utils/locale';
import { isBRServer } from '@/utils/Validators/validation';
import logger from '@/utils/logger';
import jwt from 'jsonwebtoken';

function* getVpsOciRequestSaga(action) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { hostingId } = action.payload;

  yield put(vpsOciActions.data.loading(true));

  const request = {
    url: PRODUCT_VPSOCI_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    const vpsData = response.data;

    yield put(vpsOciActions.data.set(vpsData));
  } catch (error) {
    yield put(vpsOciActions.data.set({ data: null }));
    yield put(commonActions.notifications.set({
      label: locale('productDetails.vpsOci.messages.vpsNotFound'),
      type: 'error',
    }));
  } finally {
    yield put(vpsOciActions.data.loading(false));
  }
}

function* getVpsOciStatusRequestSaga(action) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { hostingId } = action.payload;

  yield put(vpsOciActions.status.loading(true));

  const request = {
    url: PRODUCT_VPSOCI_STATUS_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    const data = response.data && response.data.data;

    yield put(vpsOciActions.status.set(data?.status ?? null));
  } catch (error) {
    yield put(vpsOciActions.status.set(null));
  } finally {
    yield put(vpsOciActions.status.loading(false));
  }
}

function* rebootVpsRequestSaga(action) {
  const hostingId = action.payload;
  const { token } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: PRODUCT_REBOOT_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      brand,
    },
  };

  yield put(vpsOciActions.reboot.loading(true));

  try {
    yield call(api.whmcs.post, request);
    yield put(
      commonActions.notifications.set({
        toast: {
          title: null,
          showIcon: true,
          info: locale('productDetails.vpsOci.myPlan.feedback.success'),
          context: 'positive',
        },
      }),
    );
  } catch (error) {
    logger.error('Error sagas rebootVpsRequestSaga', error);
    yield put(
      commonActions.notifications.set({
        toast: {
          title: null,
          showIcon: true,
          info: locale('productDetails.vpsOci.myPlan.feedback.error'),
          context: 'error',
        },
      }),
    );
  } finally {
    yield put(vpsOciActions.reboot.loading(false));
  }
}

function* rebuildVpsRequestSaga(action) {
  const hostingId = action.payload;
  const { token } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: PRODUCT_VPSOCI_REBUILD_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      brand,
    },
  };

  yield put(vpsOciActions.rebuild.loading(true));

  try {
    yield call(api.whmcs.post, request);
    yield put(vpsOciActions.rebuild.status('success'));
  } catch (error) {
    logger.error('Error sagas rebuildVpsRequestSaga', error);

    if (error.response) {
      const { status } = error.response;
      if (status === 422) {
        yield put(vpsOciActions.rebuild.status('server_inactive'));
      } else if (status === 403) {
        yield put(vpsOciActions.rebuild.status('limit_exceeded'));
      } else if (status === 404) {
        yield put(vpsOciActions.rebuild.status('not_found'));
      } else {
        yield put(vpsOciActions.rebuild.status('unknown_error'));
      }
    } else {
      yield put(vpsOciActions.rebuild.status('unknown_error'));
    }
  } finally {
    yield put(vpsOciActions.rebuild.loading(false));
  }
}

function* changeImageVpsRequestSaga(action) {
  const {
    hostingId,
    newImageId,
    imageType,
    billingCycle,
    domain,
  } = action.payload;

  let body = {
    hostingId,
    newImageId,
    imageType,
  };

  if (billingCycle) {
    body = {
      ...body,
      billingCycle,
    };
  }

  if (domain) {
    body = {
      ...body,
      domain,
    };
  }

  const { token } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: PRODUCT_VPSOCI_CHANGE_IMAGE_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      brand,
    },
    data: body,
  };

  yield put(vpsOciActions.changeImage.loading(true));

  try {
    const result = yield call(api.whmcs.post, request);
    const data = result && result.data && result.data.data;
    yield put(vpsOciActions.changeImage.set(data));
    yield put(vpsOciActions.changeImage.status('success'));
  } catch (error) {
    logger.error('Error sagas changeImageVpsRequestSaga', error);

    if (error.response) {
      const { status } = error.response;
      if (status === 422) {
        yield put(vpsOciActions.changeImage.status('server_inactive'));
      } else if (status === 403) {
        yield put(vpsOciActions.changeImage.status('limit_exceeded'));
      } else if (status === 404) {
        yield put(vpsOciActions.changeImage.status('not_found'));
      } else {
        yield put(vpsOciActions.changeImage.status('unknown_error'));
      }
    } else {
      yield put(vpsOciActions.changeImage.status('unknown_error_change_image'));
    }
    yield put(vpsOciActions.changeImage.loading(false));
  }
}

function* openSshTerminalRequestSaga(action) {
  const { hostingId } = action.payload;
  const { token } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: PRODUCT_OPEN_SSH_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      brand,
    },
  };

  yield put(vpsOciActions.openSshTerminal.loading(true));

  try {
    const response = yield call(api.whmcs.get, request);
    const { url } = response.data && response.data.data;

    yield put(vpsOciActions.openSshTerminal.set(url));
    window.open(url, '_blank');
  } catch (error) {
    logger.error('Error sagas openSshTerminalRequestSaga', error);
    yield put(vpsOciActions.openSshTerminal.set('error'));
  } finally {
    yield put(vpsOciActions.openSshTerminal.loading(false));
  }
}

function* saveSshPasswordRequestSaga(action) {
  const { hostingId, password, confirmPassword } = action.payload;
  const { token } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: PRODUCT_SAVE_PASSWORD_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      brand,
    },
    data: {
      password,
      confirmPassword,
    },
  };

  yield put(vpsOciActions.saveSshPassword.loading(true));
  yield put(vpsOciActions.saveSshPassword.success(false));

  try {
    yield call(api.whmcs.put, request);
    yield put(vpsOciActions.saveSshPassword.success(true));
    yield put(
      commonActions.notifications.set({
        toast: {
          title: null,
          showIcon: true,
          info: locale('productDetails.vpsOci.infoSsh.changePasswordModal.success'),
          context: 'positive',
        },
      }),
    );
  } catch (error) {
    logger.error('Error sagas saveSshPasswordRequestSaga', error);
    yield put(vpsOciActions.saveSshPassword.success(false));
    yield put(
      commonActions.notifications.set({
        toast: {
          title: null,
          showIcon: true,
          info: locale('productDetails.vpsOci.infoSsh.changePasswordModal.error'),
          context: 'error',
        },
      }),
    );
  } finally {
    yield put(vpsOciActions.saveSshPassword.loading(false));
  }
}

function* getTourSaga(action) {
  const { hostingId } = action.payload;
  const { token } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: PRODUCT_VPSOCI_TOUR_DETAIL_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      brand,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    const canShowTour = response.data && response.data.data && response.data.data.success;
    yield put(vpsOciActions.tour.set(canShowTour));
  } catch (error) {
    logger.error('Error sagas getTourSaga', error);
  }
}

function* saveTourStepSaga(action) {
  const { step, hostingId, session } = action.payload;
  const { token } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';
  const tokenData = jwt.decode(session, { complete: true });
  const jwtSessionId = tokenData && tokenData.payload && tokenData.payload.jwt_session_id;

  const request = {
    url: PRODUCT_VPSOCI_TOUR_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      brand,
    },
    data: {
      hostingId,
      stepName: step,
      session: jwtSessionId,
    },
  };

  try {
    yield call(api.whmcs.post, request);
  } catch (error) {
    logger.error('Error sagas saveTourStepSaga', error);
  }
}

function* getListingUpgradeVpsOciSaga(action) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const hostingId = action.payload;

  const request = {
    url: PRODUCT_VPSOCI_UPGRADE_LISTING_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };
  yield put(vpsOciActions.upgrade.listing.loading(true));
  try {
    const response = yield call(api.whmcs.get, request);
    const data = response.data && response.data.data;

    yield put(vpsOciActions.upgrade.listing.set(data));
  } catch (error) {
    yield put(vpsOciActions.upgrade.listing.set([]));
    const message = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message;
    if (message === 'This VPS package has no available upgrades.') {
      yield put(
        commonActions.notifications.set({
          toast: {
            title: null,
            showIcon: true,
            info: locale('productDetails.vpsOci.planDetails.upgrade.listing.messages.noAvailablePlans'),
            context: 'error',
          },
        }),
      );
      return;
    }
    yield put(
      commonActions.notifications.set({
        toast: {
          title: null,
          showIcon: true,
          info: locale('productDetails.vpsOci.planDetails.upgrade.listing.messages.error'),
          context: 'error',
        },
      }),
    );
  } finally {
    yield put(vpsOciActions.upgrade.listing.loading(false));
  }
}

function* getDetailsUpgradeVpsOciSaga(action) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const {
    hostingId, productId, billingCycle, coupon,
  } = action.payload;

  const request = {
    url: PRODUCT_VPSOCI_UPGRADE_DETAILS_URL(hostingId, productId),
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
    data: {
      billingCycle,
      coupon,
    },
  };
  yield put(vpsOciActions.upgrade.details.loading(true));
  try {
    const response = yield call(api.whmcs.post, request);
    const data = response.data && response.data.data;

    yield put(vpsOciActions.upgrade.details.set(data));
  } catch (error) {
    yield put(vpsOciActions.upgrade.details.set([]));
    const message = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message;
    if (message === 'This VPS package has no available upgrades.') {
      yield put(
        commonActions.notifications.set({
          toast: {
            title: null,
            showIcon: true,
            info: locale('productDetails.vpsOci.planDetails.upgrade.listing.messages.noAvailablePlans'),
            context: 'error',
          },
        }),
      );
      return;
    }
    yield put(
      commonActions.notifications.set({
        toast: {
          title: null,
          showIcon: true,
          info: locale('productDetails.vpsOci.planDetails.upgrade.listing.messages.error'),
          context: 'error',
        },
      }),
    );
  } finally {
    yield put(vpsOciActions.upgrade.details.loading(false));
  }
}

function* upgradeVpsOciSaga(action) {
  const { token } = yield select(state => state.auth);
  const { hostingId, data } = action.payload;
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: PRODUCT_VPSOCI_UPGRADE_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      brand,
    },
    data,
  };

  yield put(vpsOciActions.upgrade.submit.loading(true));

  try {
    const result = yield call(api.whmcs.post, request);
    const data = result && result.data && result.data.data;
    yield put(vpsOciActions.upgrade.submit.set(data));
  } catch (error) {
    logger.error('Error sagas upgradeVpsOciSaga', error);
    yield put(
      commonActions.notifications.set({
        toast: {
          title: null,
          showIcon: true,
          info: locale('productDetails.vpsOci.planDetails.upgrade.details.messages.error'),
          context: 'error',
        },
      }),
    );
  } finally {
    yield put(vpsOciActions.upgrade.submit.loading(false));
  }
}

function* saveTokenOpenClawSaga(action) {
  const { token } = yield select(state => state.auth);
  const { hostingId, data } = action.payload;

  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: PRODUCT_VPSOCI_CONFIGURE_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      brand,
    },
    data,
  };

  yield put(vpsOciActions.openClaw.loading(true));

  try {
    yield call(api.whmcs.post, request);
    yield put(vpsOciActions.openClaw.success(true));
    if (data) {
      yield put(
        commonActions.notifications.set({
          toast: {
            title: null,
            showIcon: true,
            info: locale('productDetails.vpsOci.openClaw.confirmationTokenPage.messages.success'),
            context: 'positive',
          },
        }),
      );
    }
  } catch (error) {
    logger.error('Error sagas saveTokenOpenClawSaga', error);
    yield put(vpsOciActions.openClaw.success(false));
    yield put(
      commonActions.notifications.set({
        toast: {
          title: null,
          showIcon: true,
          info: locale('productDetails.vpsOci.openClaw.confirmationTokenPage.messages.error'),
          context: 'error',
        },
      }),
    );
  } finally {
    yield put(vpsOciActions.openClaw.loading(false));
  }
}

function* configureGatorClawSaga(action) {
  const { token } = yield select(state => state.auth);
  const { hostingId } = action.payload;

  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: PRODUCT_VPSOCI_CONFIGURE_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      brand,
    },
    data: null,
  };

  yield put(vpsOciActions.gatorClaw.loading(true));

  try {
    yield call(api.whmcs.post, request);
    yield put(vpsOciActions.gatorClaw.success(true));
  } catch (error) {
    logger.error('Error sagas configureGatorClawSaga', error);
    yield put(vpsOciActions.gatorClaw.success(false));
  } finally {
    yield put(vpsOciActions.gatorClaw.loading(false));
  }
}

function* sagas() {
  yield takeLatest(vpsOciTypes.GET_VPS_OCI_REQUEST, getVpsOciRequestSaga);
  yield takeLatest(vpsOciTypes.STATUS_VPS_OCI_REQUEST, getVpsOciStatusRequestSaga);

  yield takeLatest(vpsOciTypes.REBOOT_VPS_REQUEST, rebootVpsRequestSaga);
  yield takeLatest(vpsOciTypes.REBUILD_VPS_REQUEST, rebuildVpsRequestSaga);

  yield takeLatest(vpsOciTypes.CHANGE_IMAGE_VPS_REQUEST, changeImageVpsRequestSaga);

  yield takeLatest(vpsOciTypes.SAVE_SSH_PASSWORD_REQUEST, saveSshPasswordRequestSaga);
  yield takeLatest(vpsOciTypes.OPEN_SSH_TERMINAL_REQUEST, openSshTerminalRequestSaga);

  yield takeLatest(vpsOciTypes.SAVE_TOUR_STEP_REQUEST, saveTourStepSaga);
  yield takeLatest(vpsOciTypes.GET_TOUR_REQUEST, getTourSaga);

  yield takeLatest(vpsOciTypes.GET_LISTING_UPGRADE_VPS_OCI, getListingUpgradeVpsOciSaga);
  yield takeLatest(vpsOciTypes.GET_DETAILS_UPGRADE_VPS_OCI, getDetailsUpgradeVpsOciSaga);
  yield takeLatest(vpsOciTypes.UPGRADE_VPS_OCI, upgradeVpsOciSaga);
  yield takeLatest(vpsOciTypes.SAVE_TOKEN_OPENCLAW_REQUEST, saveTokenOpenClawSaga);
  yield takeLatest(vpsOciTypes.CONFIGURE_GATORCLAW_REQUEST, configureGatorClawSaga);
}

export default sagas;
